import { IconButton, CustomButton } from '@/shared/ui/components';
import { validIcon } from '@/shared/ui/assets';
import classNames from 'classnames';
import _ from 'lodash';
import { memo } from 'react';
import { t } from 'ttag';
import HeaderRightButtons from '../layout/headerRightButtons';
import PriorityChar from '../layout/priorityChar/priorityChar';
import type { EventListItemHeaderProps } from './header.types';
import './header.scss';

const EMPTY_ARRAY: any[] = [];

const EventListItemHeader = ({
  checked = false,
  titleBlockArray = EMPTY_ARRAY,
  buttonList = EMPTY_ARRAY,
  isHideCheckBox = false,
  isNotCanEdit = false,
  lableText,
  hideCheckBoxClassName = '',
  checkedLine = true,
  subRight,
  className,
  classNameRight,
  isShowButtonEditReport,
  data,
}: EventListItemHeaderProps) => {
  /**
   * Renders the leading checkbox slot of an event list item header. Applies the hidden class when the checkbox is suppressed, otherwise the item-checkbox class plus the caller's className.
   *
   * @returns The checkbox slot element.
   */
  const renderCheckBox = () => (
    <div
      className={classNames(isHideCheckBox ? 'hidden' : 'item-checkbox', hideCheckBoxClassName)}
    />
  );

  /**
   * Renders the title blocks of an event list item header from titleBlockArray. Each entry becomes a column showing a priority badge, a validity label with icon, a text button, or plain content with an optional priority character and label, while missing entries render an empty div.
   *
   * @returns Array of header block elements.
   */
  const renderBlockArr = () =>
    _.map(titleBlockArray, (item, index) =>
      item ? (
        <div
          key={index}
          className={classNames(
            'item-header__group flex-col__is_jc',
            index === 0 ? 'left' : index !== titleBlockArray.length - 1 ? 'justify-center' : '',
            item.classNameDiv,
          )}
        >
          {item.title === 'Priority' && item.content ? (
            <div className={classNames('header-priority', item.className)}>
              <span>{item.content}</span>
            </div>
          ) : (
            <div>
              {!!item.title && <span className="title">{item.title}</span>}
              {item.isValidity ? (
                <div className="validity-label">
                  <img src={validIcon} alt="Valid icon" />

                  <span className="validity-content">{item.content}</span>
                </div>
              ) : (
                <div className={classNames(item.content ? 'flex' : 'dn')}>
                  {item.isTextButton ? (
                    <CustomButton
                      id={`text-btn-${index}`}
                      size="small"
                      type="outline"
                      style="default"
                      onClick={item.handleClick}
                    >
                      {item.content}
                    </CustomButton>
                  ) : (
                    <div className="flex-row">
                      <span
                        className={classNames(
                          'normal-content',
                          item.isRedLabel ? 'accent-red-label' : '',
                        )}
                      >
                        {item.content}
                      </span>

                      {item.priority && (
                        <PriorityChar char={item.priority.charAt(0)} className={item.className} />
                      )}
                    </div>
                  )}

                  {!!item.label && <div className="primary-tint-label">{item.label}</div>}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div />
      ),
    );

  /**
   * Renders the secondary right-hand title of an event list item header. Emits a clickable button when subRight is of BUTTON type, otherwise a plain text container.
   *
   * @returns The sub-right title element.
   */
  const renderSubRightTitle = () => {
    if (subRight?.type === 'BUTTON') {
      return (
        <button type="button" className="sub-right-title-btn" onClick={subRight?.handleClick}>
          <span>{subRight?.title}</span>
        </button>
      );
    }
    return (
      <div className="sub-right-title">
        <span>{subRight?.title}</span>
      </div>
    );
  };

  return (
    <div
      className={classNames(
        'item-header flex-row__jb',
        checked && checkedLine ? '--has-color-border' : '',
        className,
      )}
    >
      <div className="item-header-left flex-row__ic">
        {renderCheckBox()}

        {renderBlockArr()}
      </div>

      <div className={classNames('item-header-right flex-row__ic', classNameRight)}>
        {!_.isEmpty(subRight) && renderSubRightTitle()}

        {_.map(
          buttonList,
          (button, index) =>
            !_.isEmpty(button) &&
            (button.iconButton ? (
              <IconButton
                id={`back-${index}-btn`}
                className="back-icon-btn"
                isFilled
                iconComponent={<img src={button.iconButton} alt="Back icon" />}
                onClick={button.handleClick}
                isDebounceClick={button.isDebounceClick}
                aria-label={t`Back`}
              />
            ) : (
              <CustomButton
                id={`click-${index}-btn`}
                size="small"
                style="default"
                type="outline"
                onClick={button.handleClick}
                disabled={isNotCanEdit && button.canBeDisabled}
              >
                {button.name}
              </CustomButton>
            )),
        )}

        {lableText && (
          <span className={classNames('lable-normal', lableText.className)}>{lableText.label}</span>
        )}

        {isShowButtonEditReport && (
          <HeaderRightButtons
            data={data}
            conflictUsers={data.conflictUsers}
            isReportsPage
            limitUserCount={3}
          />
        )}
      </div>
    </div>
  );
};

export default memo(EventListItemHeader);
